import { useCallback, useEffect, useMemo, useRef, type MouseEventHandler, type PointerEventHandler, type TouchEventHandler } from 'react';

// Lang indrukken op een aanraakscherm, de tegenhanger van de rechtsklik: na `ms` milliseconden stil
// vasthouden roept de hook `onLongPress` aan met de plek van de vinger en het element waarop die
// neerkwam (zo kan één hook ook op een hele lijst zitten), trilt het toestel kort waar dat kan en
// slikt hij de klik van het loslaten, zodat de rij niet óók opent. Bewegen van meer dan `move` px
// (scrollen), eerder loslaten of een pointercancel breekt af. Muis en pen doen niets: die houden de
// rechtsklik. Zet alle handlers op het element (`{...press}`), ook onTouchEnd.

export interface LongPressHandlers {
  onPointerDown: PointerEventHandler<HTMLElement>;
  onPointerMove: PointerEventHandler<HTMLElement>;
  onPointerUp: PointerEventHandler<HTMLElement>;
  onPointerCancel: PointerEventHandler<HTMLElement>;
  onTouchEnd: TouchEventHandler<HTMLElement>;
  onClickCapture: MouseEventHandler<HTMLElement>;
}

export function useLongPress(onLongPress: (x: number, y: number, target: Element) => void, opts?: { ms?: number | undefined; move?: number | undefined }): LongPressHandlers {
  const ms = opts?.ms ?? 450;
  const move = opts?.move ?? 8;
  const callback = useRef(onLongPress);
  useEffect(() => {
    callback.current = onLongPress;
  });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  /** Zojuist afgegaan: de klik die bij dit indrukken hoort is niet meer voor de rij. */
  const fired = useRef(false);

  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  }, []);
  useEffect(() => cancel, [cancel]);

  return useMemo<LongPressHandlers>(
    () => ({
      onPointerDown: (e) => {
        cancel();
        fired.current = false;
        if (e.pointerType !== 'touch') return;
        const x = e.clientX;
        const y = e.clientY;
        const target = e.target instanceof Element ? e.target : e.currentTarget;
        start.current = { x, y };
        timer.current = setTimeout(() => {
          timer.current = null;
          start.current = null;
          fired.current = true;
          navigator.vibrate?.(10);
          callback.current(x, y, target);
        }, ms);
      },
      onPointerMove: (e) => {
        const s = start.current;
        if (s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > move) cancel();
      },
      onPointerUp: cancel,
      onPointerCancel: cancel,
      // Na het afgaan ligt het vel onder de vinger: zonder dit landt de klik van iOS op het vel.
      onTouchEnd: (e) => {
        if (fired.current) e.preventDefault();
      },
      onClickCapture: (e) => {
        if (!fired.current) return;
        fired.current = false;
        e.preventDefault();
        e.stopPropagation();
      },
    }),
    [cancel, ms, move],
  );
}
